import {skipToken} from '@reduxjs/toolkit/query';
import {Button, FormControl} from 'platform/components';
import {HStack, Show, VStack} from 'platform/foundation';

import {useState} from 'react';
import {UseFormReturn, useWatch} from 'react-hook-form';

import {isNil, isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {Locale} from '@dms/api/base';
import {TemplateContextEnum, useListContextParametersQuery} from '@dms/api/core';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {
  AiComposerEditor,
  AiComposerSelectionActions,
  AiComposerAppliedStatus,
  AiMessageComposerPanel,
  getAiComposerEditorHighlights,
  toAiComposerPlaceholders,
  useAiMessageComposer,
} from '@dms/shared';

import {Nullish} from 'shared/web';

import {FormValues} from '../types/FormValues';
import {LocaleTemplateHeading} from './LocaleTemplateHeading';
import {TemplateBodyField} from './TemplateBodyField';

interface EmailLocaleTemplateGroupProps {
  control: FormControl<FormValues>;
  formApi: UseFormReturn<FormValues>;
  context: TemplateContextEnum | Nullish;
  locale: Locale;
  isChannelDisabled: boolean;
  isPreviewDisabled: boolean;
  isLocaleHeadingVisible: boolean;
  onPreview: (locale: Locale) => void;
}

// The body is watched per locale, not as a whole record, so a keystroke in one language
// re-renders only its own group.
export function EmailLocaleTemplateGroup(props: EmailLocaleTemplateGroupProps) {
  const [body, subject] = useWatch({
    control: props.control,
    name: [`emailBodies.${props.locale}`, `emailSubjects.${props.locale}`],
  });

  const isBodyEmpty = isNilOrEmpty(body);

  const [bodyEditor, setBodyEditor] = useState<AiComposerEditor>(null);

  // The same query TemplateBodyField renders the chips from; RTK Query serves it from cache.
  const contextParameters = useListContextParametersQuery(
    isNotNil(props.context) ? {context: props.context} : skipToken
  );

  const composer = useAiMessageComposer({
    channel: 'email',
    bodyFormat: 'PLAIN_TEXT',
    draft: {body: body ?? '', subject: subject ?? undefined},
    isDisabled: props.isChannelDisabled,
    // Each group is one locale; without it the server falls back to the tenant language.
    locale: props.locale,
    placeholders: toAiComposerPlaceholders(contextParameters.currentData),
    editor: bodyEditor,
    onApply: (draft) => {
      props.formApi.setValue(`emailBodies.${props.locale}`, draft.body);
      if (isNotNil(draft.subject)) {
        props.formApi.setValue(`emailSubjects.${props.locale}`, draft.subject);
      }
    },
  });

  const composerTestId = testIds.settings.scheduledMessagesDetail(
    `emailAiComposer-${props.locale}`
  );

  return (
    <VStack spacing={4}>
      <Show when={props.isLocaleHeadingVisible}>
        <LocaleTemplateHeading
          locale={props.locale}
          data-testid={testIds.settings.scheduledMessagesDetail(`emailLocale-${props.locale}`)}
        />
      </Show>

      <TemplateBodyField
        name={`emailSubjects.${props.locale}`}
        control={props.control}
        context={props.context}
        label={i18n.t('general.labels.subject')}
        isDisabled={props.isChannelDisabled}
        minHeight={8}
      />
      <TemplateBodyField
        control={props.control}
        name={`emailBodies.${props.locale}`}
        context={props.context}
        label={i18n.t('page.messageTemplates.labels.message')}
        isDisabled={props.isChannelDisabled}
        minHeight={25}
        onEditorReady={setBodyEditor}
        selectionContent={
          composer.isEnabled ? (
            <AiComposerSelectionActions controller={composer} data-testid={composerTestId} />
          ) : undefined
        }
        editorHighlights={getAiComposerEditorHighlights(composer)}
        onKeyDownCapture={composer.onEditorKeyDownCapture}
        labelActions={isNotNil(composer.labelAction) ? [composer.labelAction] : []}
        footerContent={
          <AiComposerAppliedStatus controller={composer} data-testid={composerTestId} />
        }
        editorContent={
          <AiMessageComposerPanel controller={composer} data-testid={composerTestId} />
        }
      />

      <HStack justify="flex-start" spacing={2}>
        <Button
          variant="secondary"
          size="small"
          leftIcon="view"
          title={i18n.t('general.labels.emailPreview')}
          isDisabled={
            isBodyEmpty ||
            props.isChannelDisabled ||
            props.isPreviewDisabled ||
            isNil(props.context)
          }
          onClick={() => props.onPreview(props.locale)}
          data-testid={testIds.settings.scheduledMessagesDetail(`emailPreview-${props.locale}`)}
        />
      </HStack>
    </VStack>
  );
}
